/**
 * Base Component Class
 *
 * A lightweight base class for Custom Elements that provides:
 * - Automatic cleanup via AbortController
 * - Helper methods for event binding
 * - Lifecycle hooks (init, destroy)
 *
 * Usage:
 *   class MyComponent extends Component {
 *     init() {
 *       this.on(this.button, 'click', this.handleClick.bind(this));
 *     }
 *   }
 *   customElements.define('my-component', MyComponent);
 */
class Component extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
  }

  /**
   * Called when element is added to the DOM
   */
  connectedCallback() {
    // Allow subclasses to do async setup
    this.init?.();
  }

  /**
   * Called when element is removed from the DOM
   * Automatically cleans up all event listeners
   */
  disconnectedCallback() {
    this.abortController.abort();
    this.destroy?.();
  }

  /**
   * Add an event listener that will be automatically cleaned up
   * @param {Element} element - The target element
   * @param {string} event - The event type
   * @param {Function} handler - The event handler
   * @param {Object} options - Additional event listener options
   */
  on(element, event, handler, options = {}) {
    element?.addEventListener(event, handler, {
      signal: this.abortController.signal,
      ...options,
    });
  }

  /**
   * Add a delegated event listener
   * @param {Element} element - The container element
   * @param {string} event - The event type
   * @param {string} selector - CSS selector for delegation
   * @param {Function} handler - The event handler
   */
  delegate(element, event, selector, handler) {
    this.on(element, event, (e) => {
      const target = e.target.closest(selector);
      if (target && element.contains(target)) {
        handler.call(target, e, target);
      }
    });
  }

  /**
   * Query a selector within this element
   * @param {string} selector - CSS selector
   * @returns {Element|null}
   */
  $(selector) {
    return this.querySelector(selector);
  }

  /**
   * Query all matching elements within this element
   * @param {string} selector - CSS selector
   * @returns {NodeList}
   */
  $$(selector) {
    return this.querySelectorAll(selector);
  }

  /**
   * Get a data attribute value
   * @param {string} key - The data attribute key (without 'data-' prefix)
   * @returns {string|null}
   */
  data(key) {
    return this.dataset[key];
  }

  /**
   * Emit a custom event
   * @param {string} name - Event name
   * @param {Object} detail - Event detail data
   * @param {Object} options - Event options
   */
  emit(name, detail = {}, options = {}) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        cancelable: true,
        detail,
        ...options,
      })
    );
  }
}

// Make available globally
window.Component = Component;

/**
 * Drawer Component
 *
 * A slide-in panel that can be opened and closed.
 * Uses <dialog> element for accessibility.
 *
 * Attributes:
 *   - modal="false": Use non-modal mode (header stays interactive)
 *
 * Usage:
 *   <drawer-component id="my-drawer">
 *     <dialog>
 *       <button data-close>Close</button>
 *       <!-- drawer content -->
 *     </dialog>
 *   </drawer-component>
 *
 *   <button aria-controls="my-drawer">Open Drawer</button>
 */
class DrawerComponent extends Component {
  init() {
    this.dialog = this.$("dialog");
    this.isModal = this.getAttribute("modal") !== "false";
    this.openTriggers = document.querySelectorAll(
      `[aria-controls="${this.id}"]`
    );

    // Bind open triggers (toggle behavior)
    this.openTriggers.forEach((trigger) => {
      this.on(trigger, "click", () => this.toggle());
    });

    // Bind close triggers within drawer
    this.delegate(this, "click", "[data-close]", this.close.bind(this));

    // Close on backdrop click (for modal dialogs)
    this.on(this.dialog, "click", (e) => {
      if (e.target === this.dialog) {
        this.close();
      }
    });

    // Handle dialog close event (from Escape key in modal mode)
    this.on(this.dialog, "close", () => {
      this.onClose();
    });

    // For non-modal: handle Escape key manually
    if (!this.isModal) {
      this.on(document, "keydown", (e) => {
        if (e.key === "Escape" && this.isOpen) {
          this.close();
        }
      });
    }
  }

  open() {
    if (!this.dialog) return;

    if (this.isModal) {
      this.dialog.showModal();
    } else {
      this.dialog.show();
    }

    document.body.classList.add("scroll-lock");
    this.setAttribute("open", "");

    // Update trigger states
    this.openTriggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "true");
    });

    this.emit("drawer:open");
    this.onOpen?.();
  }

  close() {
    if (!this.dialog) return;

    this.dialog.close();

    // For non-modal, onClose isn't triggered automatically
    if (!this.isModal) {
      this.onClose();
    }
  }

  onClose() {
    document.body.classList.remove("scroll-lock");
    this.removeAttribute("open");

    // Update trigger states
    this.openTriggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
    });

    this.emit("drawer:close");
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  get isOpen() {
    return this.dialog?.open ?? false;
  }
}

customElements.define("drawer-component", DrawerComponent);

/**
 * Details Disclosure Component
 *
 * Enhanced <details> element with animation support.
 *
 * Usage:
 *   <details-disclosure>
 *     <details>
 *       <summary>Title</summary>
 *       <div class="details-content">Content here</div>
 *     </details>
 *   </details-disclosure>
 */
class DetailsDisclosure extends Component {
  init() {
    this.details = this.$("details");
    this.summary = this.$("summary");
    this.content =
      this.$(".details-content") ||
      this.details?.querySelector(":scope > :not(summary)");

    if (!this.details || !this.summary) return;

    this.on(this.summary, "click", this.handleClick.bind(this));
  }

  handleClick(e) {
    e.preventDefault();

    if (this.details.open) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.details.open = true;
    this.emit("details:open");
  }

  close() {
    this.details.open = false;
    this.emit("details:close");
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

/**
 * Quantity Selector Component
 *
 * A number input with increment/decrement buttons.
 *
 * Usage:
 *   <quantity-selector>
 *     <button data-action="decrement">-</button>
 *     <input type="number" value="1" min="1">
 *     <button data-action="increment">+</button>
 *   </quantity-selector>
 */
class QuantitySelector extends Component {
  init() {
    this.input = this.$('input[type="number"]');
    this.decrementBtn = this.$('[data-action="decrement"]');
    this.incrementBtn = this.$('[data-action="increment"]');

    if (!this.input) return;

    this.min = parseInt(this.input.min) || 1;
    this.max = parseInt(this.input.max) || Infinity;
    this.step = parseInt(this.input.step) || 1;

    this.on(this.decrementBtn, "click", this.decrement.bind(this));
    this.on(this.incrementBtn, "click", this.increment.bind(this));
    this.on(this.input, "change", this.handleChange.bind(this));
  }

  get value() {
    return parseInt(this.input?.value) || this.min;
  }

  set value(val) {
    if (!this.input) return;

    const newValue = Math.min(Math.max(val, this.min), this.max);
    this.input.value = newValue;
    this.updateButtons();
    this.emit("quantity:change", { value: newValue });
  }

  increment() {
    this.value = this.value + this.step;
  }

  decrement() {
    this.value = this.value - this.step;
  }

  handleChange() {
    this.value = this.value; // Clamp to min/max
  }

  updateButtons() {
    if (this.decrementBtn) {
      this.decrementBtn.disabled = this.value <= this.min;
    }
    if (this.incrementBtn) {
      this.incrementBtn.disabled = this.value >= this.max;
    }
  }
}

customElements.define("quantity-selector", QuantitySelector);

/**
 * Utility: Debounce function
 */
function debounce(fn, delay = 300) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Utility: Throttle function
 */
function throttle(fn, limit = 100) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Export utilities
window.debounce = debounce;
window.throttle = throttle;

/**
 * Section Reveal Animations
 *
 * Uses IntersectionObserver to trigger reveal animations
 * when elements with [data-reveal] scroll into view.
 */
(function initRevealAnimations() {
  // Detect if we're in the Shopify theme editor/customizer
  const isThemeEditor = window.Shopify && window.Shopify.designMode;

  // Respect reduced motion preference
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  // In theme editor, skip animations - reveal everything immediately
  // This prevents content from being hidden while editing
  if (isThemeEditor || prefersReducedMotion) {
    function revealAll(container = document) {
      const elements = container.querySelectorAll(
        "[data-reveal]:not(.revealed)"
      );
      elements.forEach((el) => el.classList.add("revealed"));
    }

    revealAll();

    // Still listen for section events to reveal new sections
    document.addEventListener("shopify:section:load", (event) => {
      revealAll(event.target);
    });
    return;
  }

  // Normal site: use IntersectionObserver for scroll-triggered animations
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = entry.target.dataset.revealDelay || 0;

          setTimeout(() => {
            entry.target.classList.add("revealed");
          }, delay);

          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    }
  );

  function observeElements(container = document) {
    const elements = container.querySelectorAll("[data-reveal]:not(.revealed)");
    elements.forEach((el) => observer.observe(el));
  }

  observeElements();
})();
